import AppBar from '@mui/material/AppBar';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import BackIcon from '@mui/icons-material/ArrowBack';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import ContentView from '~/components/ContentView';
import AvailabilityForm from '~/components/AvailabilityForm';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { settingsGetSettings2 } from '~/generated';
import messages from './messages';
import { extractData } from '~/utils/query-utils';
import { useCutoffDates } from '~/hooks/api/useCutoffDates';
import { useCreateRecurrentApplication } from '~/hooks/api/useCreateRecurrentApplication';
import { usePublisherMe } from '~/hooks/api/usePublisherMe';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';

const route = getRouteApi('/availability/create');

const AvailabilityCreate = () => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const publisherQuery = usePublisherMe();
  const cutOffDatesQuery = useCutoffDates();
  const vehicleWorkflowSettingQuery = useQuery({
    queryKey: ['settings', 'planning', 'isVehicleWorkflowActive'],
    queryFn: () =>
      extractData(
        settingsGetSettings2({
          path: {
            categoryKey: 'planning',
            key: 'isVehicleWorkflowActive',
          },
        })
      ),
  });
  const search = route.useSearch();
  const navigate = useNavigate();
  const publisherId = search.publisherId || publisherQuery.data?.id.toString();
  const mutation = useCreateRecurrentApplication(
    publisherId ? parseInt(publisherId, 10) : undefined,
    {
      onError() {
        showSnackbar({
          message: intl.formatMessage(messages.failureMessage),
        });
      },
      onSuccess() {
        showSnackbar({
          message: intl.formatMessage(messages.successMessage),
        });
        navigate({
          to: search.from || '/availability',
          search: {
            publisherId,
          },
        });
      },
    }
  );

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              <Grid>
                <IconButton
                  color="inherit"
                  href=""
                  component={Link}
                  to={search.from || `/availability?publisherId=${publisherId}`}
                  aria-label={intl.formatMessage(messages.back)}
                  size="large"
                >
                  <BackIcon />
                </IconButton>
              </Grid>
              <Grid>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  <FormattedMessage {...messages.title} />
                </Typography>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      }
      isLoading={publisherQuery.isLoading || cutOffDatesQuery.isLoading}
    >
      <AvailabilityForm
        renderDescription={({ startTime, endTime, startingDate }) => (
          <FormattedMessage
            {...messages.firstApplicationOnDate}
            values={{
              startTime,
              endTime,
              startingDate: moment(startingDate).format('L'),
            }}
          />
        )}
        onSubmit={(values) =>
          mutation.mutate({
            end: moment(values.end).format('YYYY-MM-DDTHH:mm:ss'),
            start: moment(values.start).format('YYYY-MM-DDTHH:mm:ss'),
            rule: values.rule,
            hasVehicle: values.hasVehicle || false,
          })
        }
        shouldShowVehicleAvailabilityField={
          vehicleWorkflowSettingQuery.data?.value === true
        }
      />
    </ContentView>
  );
};

export default AvailabilityCreate;
